import React, { ChangeEvent } from "react";

// @mui
import {
  Box,
  Radio,
  RadioGroup,
  BoxProps,
  RadioGroupProps,
} from "@mui/material";
import { rgbToHex, useTheme } from "@mui/material/styles";

// components
import { IcIcon } from "../IcIcon";

export type PickEvent = (
  event: ChangeEvent<HTMLInputElement>,
  picker?: boolean
) => void;
// ----------------------------------------------------------------------

export type ColorSinglePickerProps = RadioGroupProps & {
  colors?: string[];
  onPick?: PickEvent;
};

export default function ColorSinglePicker({
  colors,
  onPick,
  ...other
}: ColorSinglePickerProps) {
  const theme = useTheme();
  let _colors = new Set([
    ...Object.values(theme.palette.primary).flat(),
    ...(
      ["primary", "secondary", "info", "success", "warning", "error"] as const
    ).flatMap<string>((p) => Object.values(theme.palette[p])),
  ]);

  _colors = new Set([...[..._colors].map((c) => c.toLowerCase())]);

  return (
    <RadioGroup
      row
      {...other}
      onChange={(event) => {
        let value = event.target.value;
        if (value.startsWith("rgb")) {
          value = rgbToHex(value);
        }
        event.target.value = value;
        onPick?.(event, true);
      }}
    >
      {(colors || [..._colors]).map((color) => {
        const __color = color.startsWith("rgb")
          ? rgbToHex(color)
          : color.toLowerCase() === "#fff"
          ? "#ffffff"
          : color;
        let isWhite;
        if (__color === "#ffffff") isWhite = true;

        return (
          <Radio
            key={color}
            value={__color}
            color="default"
            icon={<IconColor sx={{}} />}
            checkedIcon={
              <IconColor
                sx={{
                  transform: "scale(1.4)",
                  "&:before": {
                    opacity: 0.48,
                    width: "100%",
                    content: "''",
                    height: "100%",
                    borderRadius: "50%",
                    position: "absolute",
                    boxShadow: "4px 4px 8px 0 currentColor",
                  },
                  "& svg": {
                    width: 12,
                    height: 12,
                    color: isWhite ? "common.black" : "common.white",
                  },
                  border: isWhite
                    ? `1px solid ${theme.palette.common.black}`
                    : undefined,
                }}
              />
            }
            sx={{
              color,
              "&:hover": { opacity: 0.72 },
            }}
          />
        );
      })}
    </RadioGroup>
  );
}

// ----------------------------------------------------------------------

export const IconColor = ({ sx, ...other }: BoxProps) => {
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        display: "flex",
        borderRadius: "50%",
        position: "relative",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "currentColor",
        transition: (theme) =>
          theme.transitions.create("all", {
            duration: theme.transitions.duration.shortest,
          }),
        ...sx,
      }}
      {...other}
    >
      <IcIcon icon={"eva:checkmark-fill"} />
    </Box>
  );
};
